import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { PaymentConsent } from "@/components/PaymentConsent";
import { StripePayment } from "@/components/StripePayment";
import { StripeReturn } from "@/components/StripeReturn";
import { BankTransfer } from "@/components/BankTransfer";
import { PickupPass } from "@/components/PickupPass";
import { CheckCircle2, Lock, Clock, ChevronLeft } from "lucide-react";

export default function OrderDetail() {
  const { number } = useParams();
  const { clearCart, t } = useApp();
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);

  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/orders/${number}`); setOrder(data); setError(""); setNow(Date.now()); return data; }
    catch (e) { setError(e.response?.data?.detail || "Não foi possível carregar a encomenda"); }
  }, [number]);
  useEffect(() => { setOrder(null); load(); const timer = setInterval(load, 4000); return () => clearInterval(timer); }, [load]);

  const stripeUpdated = useCallback(async () => {
    const data = await load();
    if (data?.installments?.[0]?.status === "paid") clearCart();
  }, [load, clearCart]);

  const paySecond = async () => {
    setPaying(true);
    try {
      await api.post(`/orders/${number}/confirm-payment`, {
        provider: order.payment_method || "paypal", installment: 2, simulated: true,
      });
      toast.success("2.ª parcela simulada — sem cobrança real");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setPaying(false); }
  };

  if (!order) return <div className="mx-auto max-w-3xl px-4 py-20 text-center" data-testid="order-loading-error">{error || "A carregar..."}</div>;

  const second = order.installments?.find((i) => i.number === 2);
  const consent = order.delivery?.customer_authorization;
  const consentValid = consent && consent.verification_at === order.delivery?.verified_at && consent.currency === order.currency && consent.amount_minor === Math.round((second?.amount || 0) * 100);
  const canPaySecond = order.status === "delivery_pending" && order.delivery?.status === "verified" && new Date(order.delivery.expires_at).getTime() > now;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <Link to="/conta" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ChevronLeft className="w-4 h-4" /> A minha conta
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Encomenda</div>
          <h1 data-testid="order-number" className="text-3xl font-display font-bold font-mono">{order.number}</h1>
        </div>
        <span data-testid="order-status" className={`px-3 py-1 rounded-full text-xs font-medium ${order.status === "delivered" ? "bg-green-500/15 text-green-600" : "bg-primary/15 text-primary"}`}>
          {t(`status.${order.status}`)}
        </span>
      </div>

      {error && <p role="alert" className="text-sm text-destructive mb-4" data-testid="order-refresh-error">{error}</p>}
      <StripeReturn number={number} onUpdate={stripeUpdated} />
      {order.payment_test_mode && <p className="text-sm bg-amber-500/10 text-amber-700 rounded-xl p-4 mb-4" data-testid="stripe-test-notice">STRIPE EM MODO TESTE — usa apenas cartões de teste; não há cobrança de dinheiro real.</p>}
      <PickupPass order={order} />
      {order.payment_simulated && <p className="text-sm bg-amber-500/10 text-amber-700 rounded-xl p-4 mb-4" data-testid="order-demo-notice">DEMONSTRAÇÃO — os pagamentos desta encomenda são simulados, sem cobrança real.</p>}
      <p className="text-sm text-muted-foreground mb-4" data-testid="order-delivery-instructions">{order.status === "delivered" ? "Entrega concluída." : order.payment_status === "paid" ? "Pagamento confirmado. Aguarda a confirmação de entrega pelo estafeta." : canPaySecond ? "O estafeta validou o teu código. Confirma a encomenda e autoriza o pagamento da 2.ª parcela antes de receberes o produto." : "A 2.ª parcela só fica disponível depois de o operador validar o QR ou o código na tua presença. Se a validação expirar, pede uma nova leitura."}</p>

      {order.payment_method === "stripe" && canPaySecond && second?.status === "unpaid" && !consentValid && <PaymentConsent key={order.delivery.verified_at} order={order} onUpdate={load} />}
      {order.payment_method === "stripe" && consentValid && canPaySecond && second?.status !== "paid" && <p className="text-sm text-primary mb-4" data-testid="customer-consent-recorded">Autorização registada. Abre o Stripe abaixo para confirmar o pagamento — nada foi cobrado pela leitura do código.</p>}
      <div className="rounded-2xl border border-border p-6 bg-card mb-4">
        <h3 className="font-display font-semibold mb-3">Parcelas</h3>
        <div className="space-y-3">
          {order.installments?.map((inst) => (
            <div key={inst.number} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 rounded-xl border border-border" data-testid={`installment-${inst.number}`}>
              <div className="flex items-center gap-3">
                {inst.status === "paid" ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                  : inst.status === "locked" ? <Lock className="w-5 h-5 text-muted-foreground" />
                  : <Clock className="w-5 h-5 text-yellow-500" />}
                <div>
                  <div className="font-semibold text-sm">{inst.number}ª parcela</div>
                  <div className="text-xs text-muted-foreground">
                    {inst.status === "paid" && `Paga em ${new Date(inst.paid_at).toLocaleDateString("pt-PT")}`}
                    {inst.status === "unpaid" && "Aguarda pagamento"}
                    {inst.status === "locked" && "Aguarda validação presencial do QR/código"}
                    {inst.status === "pending_review" && "Transferência em análise pelo administrador"}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold">{inst.amount.toFixed(2)} {inst.currency}</div>
                {order.payment_method === "stripe" && inst.status === "unpaid" && (inst.number === 1 || (canPaySecond && consentValid)) && !["cancelled", "refunded", "delivered"].includes(order.status) && <StripePayment order={order} installment={inst} onPaid={stripeUpdated} />}
                {inst.number === 2 && inst.status === "unpaid" && order.payment_method === "paypal" && canPaySecond && (
                  <Button size="sm" className="rounded-full btn-primary mt-2" onClick={paySecond} disabled={paying} data-testid="pay-second-btn">
                    {paying ? "A processar…" : "Simular 2.ª parcela (demo)"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        {order.payment_method === "iban" && <div className="mt-4 space-y-3">{order.installments?.filter((inst) => inst.status !== "paid").map((inst) => <BankTransfer key={inst.number} order={order} installment={inst} onUpdate={load} />)}</div>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border p-5 bg-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Entrega</div>
          <div className="text-sm">{order.shipping_address.full_name}</div>
          {order.shipping_address.country === "AO" ? (
            <>
              <div className="text-sm text-muted-foreground">📞 {order.shipping_address.phone}</div>
              <div className="text-sm text-muted-foreground">🪪 BI: {order.shipping_address.bi_number}</div>
              <div className="text-sm text-muted-foreground mt-1">🇦🇴 Angola — morada combinada por telefone</div>
            </>
          ) : (
            <>
              <div className="text-sm text-muted-foreground">{order.shipping_address.line1}</div>
              <div className="text-sm text-muted-foreground">{order.shipping_address.postal_code} {order.shipping_address.city}</div>
              {order.shipping_address.nif && <div className="text-sm text-muted-foreground">NIF: {order.shipping_address.nif}</div>}
            </>
          )}
        </div>
        <div className="rounded-2xl border border-border p-5 bg-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Items</div>
          {order.items?.map((it, i) => (
            <div key={i} className="text-sm flex justify-between">
              <span>{it.name} × {it.quantity}</span>
              <span className="font-mono">{(it.unit_price_eur * it.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="mt-3 pt-3 border-t border-border flex justify-between font-semibold">
            <span>Total</span><span className="font-mono">{order.pricing.total.toFixed(2)} {order.currency}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
